/**
 * FlightsConfig — operator UI for the flight-tracker's watches (CHO-769).
 *
 * The page owns all data flow: GET /api/flights/watches via react-query, and
 * plain same-origin fetch POST/PUT mutations (the am_session cookie set by
 * AuthGate authenticates them, same pattern as the payments pages). Every
 * write is relayed by the server to the flight-tracker CLI — the tracker's
 * single validated write surface; the CLI's stderr comes back in `error` and
 * is displayed verbatim. Rendering lives in the prop-driven components under
 * components/flights/ (see the /dev/components gallery).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WatchListTable } from '../../components/flights/WatchListTable';
import { WatchForm } from '../../components/flights/WatchForm';
import type { TrackerWatch, WatchesResponse, WatchDocument } from '../../types/flights';

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return res.json() as Promise<T>;
}

/** POST/PUT to the watches API. Throws Error(data.error) so the tracker's
 *  stderr travels verbatim into the form's alert region. */
async function mutate(url: string, method: 'POST' | 'PUT', body?: unknown): Promise<void> {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
}

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

type EditorState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; watch: TrackerWatch };

const UNAVAILABLE_EMPTY_STATE =
  'Tracker watches table not found — the flight-tracker with runtime watch config (CHO-768) has not run yet on this machine.';

export default function FlightsConfig() {
  const queryClient = useQueryClient();
  const watches = useQuery({
    queryKey: ['flights', 'watches'],
    queryFn: () => getJson<WatchesResponse>('/api/flights/watches'),
  });

  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  /** The editor open RIGHT NOW. A toggle that completes after the operator
   *  switched editors must not sync the lifted enabled state into the new
   *  editor's form — the async continuation's `editor` closure is the stale
   *  render from when the toggle started (review finding: enabling A, opening
   *  B mid-flight, A's completion flipped B's checkbox). */
  const editorRef = useRef<EditorState>(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Per-watch pending table toggles (CHO-769 review round 4): a single busy
   *  name cannot track concurrent toggles — starting B while A is pending
   *  unlocked A's row, and either completion cleared the other's busy state,
   *  so a row could be toggled twice mid-flight. Every toggle tracks its own
   *  watch; a completion only clears its own name. */
  const [pendingToggles, setPendingToggles] = useState<ReadonlySet<string>>(new Set());
  /** Enabled state of the watch open in the editor, LIFTED from WatchForm so a
   *  table toggle of the same watch updates the form's checkbox (and the saved
   *  payload) instead of the stale editor snapshot winning on save. */
  const [editorEnabled, setEditorEnabled] = useState(true);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['flights', 'watches'] });

  const submitForm = async (doc: WatchDocument) => {
    setSubmitting(true);
    setError(null);
    try {
      if (editor.mode === 'edit') {
        await mutate(`/api/flights/watches/${encodeURIComponent(editor.watch.name)}`, 'PUT', doc);
        // The PUT succeeded, so its resulting document is known NOW. invalidate
        // only *requests* a refetch and SWALLOWS its errors — if the refresh
        // fails, the retained cache still holds the PRE-SAVE watch, and
        // reopening the row loads the stale dates / enabled state; the next
        // save then silently reverts this successful edit (review round 9).
        // Write the mutation result into the cache FIRST; a successful refetch
        // overwrites it with the same server truth. Server-owned timestamps
        // ride along from the previous cache entry until that refetch.
        queryClient.setQueryData<WatchesResponse>(['flights', 'watches'], (old) =>
          old && old.available && Array.isArray(old.watches)
            ? {
                ...old,
                watches: old.watches.map((w) =>
                  w.name === editor.watch.name
                    ? { ...w, ...doc, enabled: doc.enabled ?? w.enabled }
                    : w,
                ),
              }
            : old,
        );
      } else {
        await mutate('/api/flights/watches', 'POST', doc);
        // Same pin for create: without it, a failed post-create refresh leaves
        // the new watch missing from the retained cache — the operator sees no
        // result and a retry 400s on the duplicate name. The tracker defaults
        // enabled to true when the document omits it; the appended timestamps
        // are placeholders the successful refetch replaces.
        const now = new Date().toISOString();
        queryClient.setQueryData<WatchesResponse>(['flights', 'watches'], (old) =>
          old && old.available && Array.isArray(old.watches)
            ? {
                ...old,
                watches: [
                  ...old.watches.filter((w) => w.name !== doc.name),
                  { ...doc, enabled: doc.enabled ?? true, created_at: now, updated_at: now },
                ],
              }
            : old,
        );
      }
      await invalidate();
      setEditor({ mode: 'closed' });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnabled = async (watch: TrackerWatch) => {
    // Conflicts serialize in both directions: a second toggle of the same
    // watch is a no-op while one is pending, and a toggle of the watch whose
    // save is in flight must wait — the in-flight PUT carries the lifted
    // enabled state and the toggle would either undo it or be undone by it.
    if (pendingToggles.has(watch.name)) return;
    if (submitting && editor.mode === 'edit' && editor.watch.name === watch.name) return;
    setPendingToggles((prev) => new Set(prev).add(watch.name));
    setError(null);
    try {
      await mutate(
        `/api/flights/watches/${encodeURIComponent(watch.name)}/${watch.enabled ? 'disable' : 'enable'}`,
        'POST',
      );
      // The toggle succeeded, so its resulting state is known NOW. invalidate
      // only *requests* a refetch and SWALLOWS its errors — if the GET fails,
      // whatever the cache held before is what getQueryData returns below, and
      // copying that (pre-toggle) value into the editor resurrects it: disable
      // → failed refresh → recovery → save silently re-enables the watch
      // (review round 7). Write the mutation result into the cache FIRST; a
      // successful refetch still overwrites it with the same server truth.
      const nextEnabled = !watch.enabled;
      queryClient.setQueryData<WatchesResponse>(['flights', 'watches'], (old) =>
        old && old.available && Array.isArray(old.watches)
          ? {
              ...old,
              watches: old.watches.map((w) =>
                w.name === watch.name ? { ...w, enabled: nextEnabled } : w,
              ),
            }
          : old,
      );
      await invalidate();
      // Sync the lifted enabled state ONLY if the editor that is open when the
      // toggle completes is still the one that was open when it started
      // (editorRef), from the cache we just pinned to the mutation result (or
      // the refetched server truth when the refresh succeeded). If the operator
      // switched editors mid-flight, the new editor's own open handler already
      // set the right value and must not be overwritten by this completion.
      if (editorRef.current.mode === 'edit' && editorRef.current.watch.name === watch.name) {
        const fresh = queryClient.getQueryData<WatchesResponse>(['flights', 'watches']);
        const served = fresh?.watches?.find((w) => w.name === watch.name);
        setEditorEnabled(served ? served.enabled : nextEnabled);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPendingToggles((prev) => {
        const next = new Set(prev);
        next.delete(watch.name);
        return next;
      });
    }
  };

  if (watches.isPending) {
    return <p className="text-sm text-gray-500">Loading watches…</p>;
  }
  // Only an INITIAL-load failure has nothing to render. A background refresh
  // failure puts the query into its error state but RETAINS the last
  // successful data (react-query v5) — replacing the whole page with the alert
  // would unmount the open WatchForm and silently discard the operator's
  // unsaved edits (review round 8). That case renders its alert alongside the
  // stale-but-usable UI instead.
  if (watches.isError && !watches.data) {
    return (
      <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
        Could not reach the flights API: {(watches.error as Error).message}
      </div>
    );
  }

  const data = watches.data;
  if (!data) return null; // unreachable — the isPending / isError guards above both returned
  if (!data.available) {
    // Don't hide the tab — explain why there is nothing to configure yet.
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-2">Flight tracker watches</h2>
        <p className="text-sm text-gray-600">{UNAVAILABLE_EMPTY_STATE}</p>
        {data.reason && <p className="mt-2 text-xs text-gray-400 font-mono">{data.reason}</p>}
      </div>
    );
  }

  const allWatches = data.watches ?? [];

  // Rows with a mutation in flight: pending toggles, plus the watch being
  // saved (its toggle must wait for the PUT, and its Edit button reflects the
  // busy row). Toggles of every other watch stay available.
  const busyNames = new Set(pendingToggles);
  if (submitting && editor.mode === 'edit') busyNames.add(editor.watch.name);

  return (
    <div className="space-y-4">
      {error && (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 whitespace-pre-wrap">
          {error}
        </div>
      )}
      {watches.isError && data && (
        // Background-refresh failure with retained cache (see the guard above):
        // the watches shown are the last loaded ones — say so, and keep the
        // editor mounted so unsaved edits survive the outage.
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          Could not reach the flights API: {(watches.error as Error).message} — showing the last loaded watches.
        </div>
      )}
      <Card
        title="Watches"
        action={
          editor.mode === 'closed' ? (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setEditor({ mode: 'create' });
              }}
              // Editor switching is locked while a save is in flight: the
              // completion closes the editor it originated from, so a new
              // editor opened mid-save would be silently closed (or inherit
              // the save's error) — see the review round 4 finding.
              disabled={submitting}
              className="px-3 py-1.5 rounded bg-gray-900 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50"
            >
              New watch
            </button>
          ) : undefined
        }
      >
        <WatchListTable
          watches={allWatches}
          busyNames={busyNames}
          editLocked={submitting}
          onEdit={(watch) => {
            setError(null);
            setEditorEnabled(watch.enabled);
            setEditor({ mode: 'edit', watch });
          }}
          onToggleEnabled={toggleEnabled}
        />
      </Card>
      {editor.mode !== 'closed' && (
        <Card title={editor.mode === 'edit' ? `Edit watch — ${editor.watch.name}` : 'New watch'}>
          {/* key = editor identity: WatchForm initializes its useState fields once
              at mount, so switching edit targets (A → B) or create → edit must
              remount it — otherwise the stale watch's fields survive and saving
              submits the previous document to the new endpoint. `enabled` is
              lifted (not keyed) so a table toggle of the open watch updates the
              mounted form instead of remounting it and losing typed edits. */}
          <WatchForm
            key={editor.mode === 'edit' ? `edit:${editor.watch.name}` : 'create'}
            initial={editor.mode === 'edit' ? editor.watch : null}
            {...(editor.mode === 'edit'
              ? { enabled: editorEnabled, onEnabledChange: setEditorEnabled }
              : {})}
            submitting={submitting}
            // Serialize conflicting mutations: while a table toggle of the
            // open watch is in flight its enabled state is undecided, and a
            // save right now would PUT the stale value and undo the toggle.
            saveDisabled={editor.mode === 'edit' && pendingToggles.has(editor.watch.name)}
            error={error}
            // A parse error blocks onSubmit — the path that normally clears
            // `error` — so without this the page alert would keep showing the
            // previous attempt's CLI stderr while the form shows the parse
            // error that actually stopped the submission (review round 6).
            onFormErrorChange={() => setError(null)}
            onSubmit={submitForm}
            onCancel={() => setEditor({ mode: 'closed' })}
          />
        </Card>
      )}
      <p className="text-xs text-gray-400">
        Writes run through the flight-tracker CLI — the tracker's own validation rejects bad input, and every
        scheduled tracker run picks up table changes with no restart.
      </p>
    </div>
  );
}
